import { FormikField, theme } from '@ahmdigital/ui';
import Icon from '@ahmdigital/icons-web';
import styled from 'styled-components';

import { BOX_SHADOW, BUTTON_SIZE_LARGE, BUTTON_SIZE_MEDIUM } from '../../styles';
import { StepInputAppearance, WidgetButtonSpacing } from '../../types';

const { colors, media, mediaQueries } = theme;

const DESKTOP_BORDER_RADIUS = '3rem';
const BASE_BORDER_RADIUS = '1rem';
const PILL_BORDER_RADIUS = '6.5rem';
const BORDER_WIDTH = '1px';
const INPUT_PADDING_Y = '0.875rem';
const DEFAULT_MEDIUM_BUTTON_SPACING = 1.8;
const PANEL_SPACING_TO_EDGE = 1.375;
const DOUBLE_SPACING = PANEL_SPACING_TO_EDGE * 2;

const getInputWrapperDesktopStyles = ($appearance: StepInputAppearance | undefined, $isLast: boolean | undefined) => {
  const nonLastPaddingXRem = $appearance?.mdNonLastPaddingXRem;

  if (!$isLast && $appearance?.mdNonLastWidthFull) {
    return 'width: 100%;';
  }

  if (!$isLast && Number.isFinite(nonLastPaddingXRem)) {
    return `padding: 1rem ${nonLastPaddingXRem}rem;`;
  }

  return '';
};

const getLargeWidgetPaddingRight = ($buttonSpacing: WidgetButtonSpacing | undefined, $isLast: boolean | undefined) => {
  if (!$isLast) return `${PANEL_SPACING_TO_EDGE}rem`;

  return `${BUTTON_SIZE_LARGE + ($buttonSpacing?.lg ?? DOUBLE_SPACING)}rem`;
};

const getInputWrapperLargeStyles = ($buttonSpacing: WidgetButtonSpacing | undefined, $isLast: boolean | undefined) => {
  const paddingRight = getLargeWidgetPaddingRight($buttonSpacing, $isLast);
  return `padding-right: ${paddingRight};`;
};

export const FieldWrapper = styled.div<{
  $isFirst?: boolean;
  $isLast?: boolean;
  $isOpen?: boolean;
  $hasError: boolean;
}>`
  border-color: ${({ $isOpen, $hasError }) => {
    if ($hasError) return colors.red;
    if ($isOpen) return colors.purple;
    return colors.grey10;
  }};
  border-radius: ${BASE_BORDER_RADIUS};
  border-style: solid;
  border-width: ${BORDER_WIDTH};
  border-bottom-left-radius: ${({ $isOpen }) => ($isOpen ? '0' : '')};
  border-bottom-right-radius: ${({ $isOpen }) => ($isOpen ? '0' : '')};
  box-shadow: ${BOX_SHADOW};
  margin-bottom: ${({ $isOpen }) => ($isOpen ? '0' : '1rem')};
  padding: 1rem;
  width: 100%;
  background-color: ${({ $hasError }) => ($hasError ? colors.redTint : 'transparent')};

  ${mediaQueries.above(media.md)} {
    border-color: transparent;
    border-radius: ${({ $hasError }) => ($hasError ? DESKTOP_BORDER_RADIUS : 'unset')};
    box-shadow: unset;
    border-bottom-left-radius: ${({ $isFirst }) => ($isFirst ? `${DESKTOP_BORDER_RADIUS}` : '')};
    border-top-left-radius: ${({ $isFirst }) => ($isFirst ? `${DESKTOP_BORDER_RADIUS}` : '')};
    border-bottom-right-radius: ${({ $isLast }) => ($isLast ? `${DESKTOP_BORDER_RADIUS}` : '')};
    border-top-right-radius: ${({ $isLast }) => ($isLast ? `${DESKTOP_BORDER_RADIUS}` : '')};
    display: flex;
    flex: 1;
    margin-bottom: 0;
    padding: 0;
    position: relative;

    &:focus {
      border-radius: ${DESKTOP_BORDER_RADIUS};
      outline: 2px dotted ${colors.grey50};
      outline-offset: 2px;
    }

    &:hover {
      background-color: ${({ $isOpen, $hasError }) => {
        if ($hasError) return colors.redTint;
        if ($isOpen) return colors.white;
        return colors.grey10;
      }};
      border-radius: ${PILL_BORDER_RADIUS};
      cursor: pointer;
    }
  }
`;

export const IconWrapper = styled.div<{
  $appearance?: StepInputAppearance;
}>`
  padding: 0.25rem;
  display: ${({ $appearance }) => ($appearance?.centerIcon ? 'flex' : 'block')};
  align-items: ${({ $appearance }) => ($appearance?.centerIcon ? 'center' : 'unset')};
  justify-content: ${({ $appearance }) => ($appearance?.centerIcon ? 'center' : 'unset')};
`;

export const InputSeparator = styled.div<{ $isVisible: boolean; $isOpen?: boolean }>`
  display: none;

  ${mediaQueries.above(media.md)} {
    align-self: center;
    display: ${({ $isOpen }) => ($isOpen ? 'none' : 'flex')};
    border-right: ${BORDER_WIDTH} solid ${({ $isVisible }) => ($isVisible ? colors.grey20 : colors.white)};
    height: 4rem;
  }
`;

export const InnerInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 0.5rem;

  ${mediaQueries.above(media.md)} {
    justify-content: normal;
  }
`;

export const InputWrapper = styled.div<{
  $appearance?: StepInputAppearance;
  $buttonSpacing?: WidgetButtonSpacing;
  $hasError: boolean;
  $isOpen?: boolean;
  $isLast?: boolean;
}>`
  border-radius: ${PILL_BORDER_RADIUS};
  border-top-right-radius: ${({ $isOpen }) => ($isOpen ? PILL_BORDER_RADIUS : '0')};
  border-bottom-right-radius: ${({ $isOpen }) => ($isOpen ? PILL_BORDER_RADIUS : '0')};
  width: ${({ $isLast }) => ($isLast ? 'auto' : '100%')};

  ${mediaQueries.above(media.md)} {
    width: 100%;
    border-width: ${BORDER_WIDTH};
    border-style: solid;
    border-color: ${({ $isOpen, $hasError }) => {
      if ($hasError) return colors.red;
      if ($isOpen) return colors.purple;
      return 'transparent';
    }};
    box-shadow: ${({ $isOpen }) => ($isOpen ? `${BOX_SHADOW}` : 'unset')};

    padding: ${({ $buttonSpacing, $isLast }) => {
      const top = INPUT_PADDING_Y;
      let right = `${PANEL_SPACING_TO_EDGE}rem`;

      if ($isLast) {
        right = `${BUTTON_SIZE_MEDIUM + ($buttonSpacing?.md ?? DEFAULT_MEDIUM_BUTTON_SPACING)}rem`;
      }
      const bottom = top;
      const left = `${PANEL_SPACING_TO_EDGE}rem`;

      return `${top} ${right} ${bottom} ${left}`;
    }};

    ${({ $appearance, $isLast }) => getInputWrapperDesktopStyles($appearance, $isLast)}

    & input:focus {
      outline: none;
      outline-offset: none;
    }
  }

  ${mediaQueries.above(media.lg)} {
    ${({ $buttonSpacing, $isLast }) => getInputWrapperLargeStyles($buttonSpacing, $isLast)}
  }
`;

export const ModalWrapper = styled.div<{
  $isOpen: boolean | undefined;
  $hasError: boolean;
  $isFirst?: boolean;
  $isLast?: boolean;
}>`
  background-color: ${colors.white};
  border-style: solid;
  border-width: ${BORDER_WIDTH};
  border-color: ${({ $isOpen, $hasError }) => {
    if ($hasError) return colors.red;
    if ($isOpen) return colors.purple;
    return colors.grey10;
  }};
  border-top: none;
  border-bottom-left-radius: ${({ $isOpen }) => ($isOpen ? BASE_BORDER_RADIUS : '0')};
  border-bottom-right-radius: ${({ $isOpen }) => ($isOpen ? BASE_BORDER_RADIUS : '0')};
  box-shadow: ${BOX_SHADOW};
  margin-bottom: ${({ $isOpen }) => ($isOpen ? '1rem' : '0')};
  padding: 1rem;
  width: 100%;

  ${mediaQueries.above(media.md)} {
    border-radius: ${BASE_BORDER_RADIUS};
    border-color: ${colors.purple};
    border-top: ${BORDER_WIDTH} solid ${colors.purple};
    box-shadow: ${BOX_SHADOW};
    margin-top: 1rem;
    margin-bottom: 0;
    padding: 2rem;
    position: absolute;
    width: max-content;
    top: 8.25rem;
    z-index: 100;
    left: ${({ $isFirst }) => ($isFirst ? `-${PANEL_SPACING_TO_EDGE}rem` : 'unset')};
    right: ${({ $isLast }) => ($isLast ? `-${PANEL_SPACING_TO_EDGE}rem` : 'unset')};
  }

  ${mediaQueries.above(media.lg)} {
    right: unset;
  }
`;

export const StyledIcon = styled(Icon)<{ $isAnimate: boolean | undefined }>`
  transition: transform 0.25s linear;
  transform: ${({ $isAnimate }) => ($isAnimate ? 'rotate(180deg)' : 'none')};
`;

export const StyledFormikField = styled(FormikField)`
  margin-bottom: 0;
  margin-right: 1rem;
  width: 100%;

  ${mediaQueries.above(media.md)} {
    margin-right: 0;
    width: auto;
  }

  & input {
    font-size: 1.25rem;
  }
`;

/* min-width mirrors flex-basis so the flex layout honours the intended field size —
 * without this, fields shrink to their text content width. */
export type stepSizeFunction = (
  fieldWidth: number | undefined,
  isLast: boolean | undefined,
  buttonSize: number,
  fluidWidth?: boolean,
) => string;
export const stepFlexBasis: stepSizeFunction = (fieldWidth, isLast, buttonSize, fluidWidth = false) => {
  if (fluidWidth) {
    if (isLast && fieldWidth) return `1 1 ${fieldWidth + buttonSize}rem`;
    if (fieldWidth) return `1 1 ${fieldWidth}rem`;
    return '1 1 0';
  }

  if (isLast && fieldWidth) return `0 0 ${fieldWidth + buttonSize}rem`;
  if (fieldWidth) return `0 0 ${fieldWidth}rem`;
  return '0 1 auto';
};

export const stepMinWidth: stepSizeFunction = (fieldWidth, isLast, buttonSize, fluidWidth = false) => {
  if (fluidWidth) return '0';

  if (isLast && fieldWidth) return `${fieldWidth + buttonSize}rem`;
  if (fieldWidth) return `${fieldWidth}rem`;
  return 'auto';
};

export const StepWrapper = styled.div<{ $fieldWidth?: number; $fluidWidth?: boolean; $isLast?: boolean }>`
  position: relative;
  display: flex;
  flex-wrap: wrap;

  ${mediaQueries.above(media.md)} {
    flex: ${({ $fieldWidth, $fluidWidth, $isLast }) =>
      stepFlexBasis($fieldWidth, $isLast, BUTTON_SIZE_MEDIUM, $fluidWidth)};
    min-width: ${({ $fieldWidth, $isLast, $fluidWidth }) =>
      stepMinWidth($fieldWidth, $isLast, BUTTON_SIZE_MEDIUM, $fluidWidth)};
  }

  ${mediaQueries.above(media.lg)} {
    flex: ${({ $fieldWidth, $fluidWidth, $isLast }) =>
      stepFlexBasis($fieldWidth, $isLast, BUTTON_SIZE_LARGE, $fluidWidth)};
    min-width: ${({ $fieldWidth, $isLast, $fluidWidth }) =>
      stepMinWidth($fieldWidth, $isLast, BUTTON_SIZE_LARGE, $fluidWidth)};
  }
`;

export const ErrorText = styled.div`
  display: block;
  color: ${colors.redDark};
  margin-bottom: 1.5rem;

  ${mediaQueries.above(media.md)} {
    display: block;
    font-size: 14px;
    margin-bottom: 0;
    margin-top: 0.5rem;
    position: absolute;
    bottom: -1.75rem;
    width: 21.875rem;
  }
`;
